import {
  CDropdown,
  CDropdownHeader,
  CDropdownItem,
  CDropdownMenu,
  CInput,
  CInputGroup,
  CInputGroupText,
  CSpinner
} from "@coreui/react"
import { datadogRum } from "@datadog/browser-rum"
import postPinnedProjects from "api/collections/users/postPinnedProjects"
import downArrow from "assets/icons/downArrow.svg"
import searchIcon from "assets/icons/search.svg"
import classNames from "classnames"
import { CMSButton } from "components/common/button"
import { projectSelectTooltip } from "constants/StringConstants"
import { debounce } from "lodash"
import PropTypes from "prop-types"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { userActions } from "store"
import { useExtension } from "utils"
import { useUserAuth } from "utils/hooks"

import pinFilled from "../../../assets/icons/carbon_pin_filled.svg"
import pinOutline from "../../../assets/icons/carbon_pin_outline.svg"
import { Tooltip } from "../tooltip"
import CMSTypography, {
  fontSizes,
  fontWeights,
  StandardTypographyTypes
} from "../typography/CMSTypography"

function ProjectSelect({
  items,
  value,
  onChange,
  className,
  isLoading,
  isProjectSelectDropdownOpen,
  setIsProjectSelectDropdownOpen
}) {
  const [searchText, setSearchText] = useState("")
  const [displayItems, setDisplayItems] = useState(items)
  const dispatch = useDispatch()
  const extension = useExtension()
  const auth = useUserAuth()
  const email = useSelector(
    (state) => (state && state.user && state.user.email) || ""
  )
  const currentProject = useSelector((state) => {
    return (state && state.settings && state.settings.currentProject) || ""
  })

  const dropdownRef = useRef(null)
  const user = email.split("@")[0]
  const host = extension?.config?.env?.clientApiGateway?.URL

  // Limiting the filtering on every subsequent key click
  const debouncedFilterItems = useMemo(
    () =>
      debounce((projectItems, text) => {
        const filteredProjects = projectItems.filter(
          (projectItem) =>
            projectItem.text?.toLowerCase().indexOf(text.toLowerCase()) !==
            -1 ||
            projectItem.key?.toLowerCase().indexOf(text.toLowerCase()) !== -1
        )
        setDisplayItems(filteredProjects)
      }, 300),
    []
  )
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProjectSelectDropdownOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [dropdownRef, setIsProjectSelectDropdownOpen])

  useEffect(() => {
    debouncedFilterItems(items, searchText)
  }, [debouncedFilterItems, items, searchText])
  const onChangeHandler = (item) => {
    datadogRum.addAction("project", {
      id: item.key,
      name: item.text
    })
    console.log("=====Datadog=====")
    console.log({ id: item.key, name: item.text })
    onChange(item.key)
    setIsProjectSelectDropdownOpen(false)
  }

  // to pin or unpin a project
  const togglePin = (item, event) => {
    event.stopPropagation()
    dispatch(userActions.changePinnedStatus(item.text))
    for (const project of items) {
      if (project.text === item.text) {
        project.u_project_pinned = !project.u_project_pinned
        if (project.u_project_pinned) {
          onChange(item.key)
        } else if (project.key === currentProject) {
          let newCurrentProject = ""
          items.forEach((project) => {
            if (project.u_project_pinned) {
              newCurrentProject = project.key
            }
          })
          if (newCurrentProject === "") {
            onChange("PROJ0036094")
          } else {
            onChange(newCurrentProject)
          }
        }
        break
      }
    }
    const pinnedProjectsResponse = {
      pinned_projects: ""
    }
    for (const project of items) {
      if (project.u_project_pinned) {
        if (pinnedProjectsResponse.pinned_projects === "") {
          pinnedProjectsResponse.pinned_projects = project.key
        } else {
          pinnedProjectsResponse.pinned_projects += `,${project.key}`
        }
      }
    }
    postPinnedProjects(
      {
        auth,
        host,
        body: JSON.stringify(pinnedProjectsResponse)
      },
      user
    )
  }

  const selectedItem = items.find((item) => item.key === value)

  return (
    <CDropdown className={className} innerRef={dropdownRef}>
      <Tooltip
        text={projectSelectTooltip(selectedItem)}
        className="w-100"
        containerClass="bg-white mw-100"
        zIndex={1000}
      >
        <CMSButton
          type="secondary"
          disabled={isLoading}
          onClick={() => setIsProjectSelectDropdownOpen(true)}
          className="w-100"
        >
          <div className="d-flex w-100 align-items-center justify-content-between">
            {isLoading ? (
              <div className="w-100 d-flex justify-content-center">
                <CSpinner size="sm" />
              </div>
            ) : (
              <>
                <span className="text-ellipsis">
                  {selectedItem ? selectedItem.text : "No Project Selected"}
                </span>
                <img
                  src={downArrow}
                  alt="down arrow"
                  className="iconDropdownStyle"
                />
              </>
            )}
          </div>
        </CMSButton>
      </Tooltip>
      <CDropdownMenu
        className={classNames(
          "pt-0 w-100 dropdown-scrollbar mobile-view-width-project",
          {
            show: isProjectSelectDropdownOpen
          }
        )}
      >
        <CDropdownHeader className="position-sticky top-0 left-0 bg-white w-100">
          <CInputGroup>
            <CInput
              type="text"
              name="project-search"
              className="border-bottom border-top-0 border-left-0 border-right-0 rounded-0 px-0"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              data-testid="project-search-input"
            />
            <CInputGroupText className="border-bottom border-top-0 border-left-0 border-right-0 rounded-0 p-0 bg-white">
              <img src={searchIcon} alt="search icon" />
            </CInputGroupText>
          </CInputGroup>
        </CDropdownHeader>
        <div className="width-max-content min-width-100">
          {displayItems.length ? (
            displayItems.map((item, index) => (
              <CDropdownItem
                onClick={() => onChangeHandler(item)}
                className="flex-row w-100"
                key={`${item.key}-${index}`}
                title={`${item.text}\n${item.key}`}
              >
                <div
                  onClick={(event) => togglePin(item, event)}
                  className="cursor-pointer"
                  data-testid={`${item.key}-${index}-pin`}
                >
                  {items.length > 1 &&
                    ((!item.u_project_pinned && (
                      <img
                        src={pinOutline}
                        className="pin-style"
                        alt="hollow pin"
                      />
                    )) ||
                      (item.u_project_pinned && (
                        <img
                          src={pinFilled}
                          className="pin-style"
                          alt="filled pin"
                        />
                      )))}
                </div>
                <div className="flex-column align-items-start">
                  <CMSTypography type={StandardTypographyTypes.cardContent}>
                    {item.text}
                  </CMSTypography>
                  <CMSTypography
                    fontSize={fontSizes.smaller}
                    fontWeight={fontWeights.light}
                  >
                    {item.key}
                  </CMSTypography>
                </div>
              </CDropdownItem>
            ))
          ) : (
            <CDropdownItem disabled>No Projects Found</CDropdownItem>
          )}
        </div>
      </CDropdownMenu>
    </CDropdown>
  )
}

ProjectSelect.defaultProps = {
  items: [],
  onChange: () => { },
  className: "",
  buttonClassName: "",
  isLoading: false,
  isProjectSelectDropdownOpen: false,
  setIsProjectSelectDropdownOpen: () => { }
}

ProjectSelect.propTypes = {
  items: PropTypes.array,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  className: PropTypes.string,
  buttonClassName: PropTypes.string,
  isLoading: PropTypes.bool,
  isProjectSelectDropdownOpen: PropTypes.bool,
  setIsProjectSelectDropdownOpen: PropTypes.func
}

export default ProjectSelect
